import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../firebase.init";
import axios from "axios";

const NavBar = () => {
  const [user, loading, error] = useAuthState(auth);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [footerData, setFooterData] = useState({
    logo: { src: "", alt: "" },
  });
  const [footerLoading, setFooterLoading] = useState(true);
  const [footerError, setFooterError] = useState(null);

  // Cache keys for localStorage
  const CACHE_KEYS = {
    USER_DATA: "navbar_user_data",
    FOOTER_DATA: "navbar_footer_data",
    AUTH_STATE: "navbar_auth_state",
  };

  // Load cached data on component mount
  useEffect(() => {
    const loadCachedData = () => {
      try {
        // Load cached user data
        const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
        if (cachedUserData) {
          setUserData(JSON.parse(cachedUserData));
        }
        // Load cached footer data
        const cachedFooterData = localStorage.getItem(CACHE_KEYS.FOOTER_DATA);
        if (cachedFooterData) {
          setFooterData(JSON.parse(cachedFooterData));
          setFooterLoading(false);
        }
        // Load cached auth state
        const cachedAuthState = localStorage.getItem(CACHE_KEYS.AUTH_STATE);
        if (cachedAuthState) {
          const authData = JSON.parse(cachedAuthState);
          // Only use cached auth state if it's recent (less than 5 minutes old)
          if (Date.now() - authData.timestamp < 5 * 60 * 1000) {
            setAuthLoading(false);
          }
        }
      } catch (error) {
        console.error("Error loading cached data:", error);
      }
    };
    loadCachedData();
  }, []);

  // Handle Firebase auth state changes
  useEffect(() => {
    if (!loading) {
      setAuthLoading(false);
      // Cache auth state
      try {
        const authStateData = {
          hasUser: !!user,
          timestamp: Date.now(),
          userEmail: user?.email || null,
        };
        localStorage.setItem(
          CACHE_KEYS.AUTH_STATE,
          JSON.stringify(authStateData)
        );
      } catch (error) {
        console.error("Error caching auth state:", error);
      }
    }
  }, [user, loading]);

  // Fetch footer data with caching
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        setFooterLoading(true);
        const response = await axios.get(
          "https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/api/footer-section"
        );
        if (response.data.success) {
          // Transform API data to match our component structure
          const apiData = response.data.data;
          const transformedData = {
            ...apiData,
            logo: {
              src: apiData.middleSection?.logoUrl || "",
              alt: apiData.topSection?.logoText || "Aithm",
            },
          };

          setFooterData(transformedData);

          // Cache footer data
          try {
            localStorage.setItem(
              CACHE_KEYS.FOOTER_DATA,
              JSON.stringify(transformedData)
            );
            localStorage.setItem(
              "footer_data_timestamp",
              Date.now().toString()
            );
          } catch (error) {
            console.error("Error caching footer data:", error);
          }
        } else {
          setFooterError("Failed to fetch footer data");
        }
      } catch (err) {
        console.error("Error fetching footer data:", err);
        setFooterError("Error fetching footer data");
      } finally {
        setFooterLoading(false);
      }
    };

    // Only fetch if we don't have cached data or if it's been more than 1 hour
    const cachedFooterData = localStorage.getItem(CACHE_KEYS.FOOTER_DATA);
    const lastFetchTime = localStorage.getItem("footer_data_timestamp") || "0";
    const shouldFetch =
      !cachedFooterData ||
      Date.now() - parseInt(lastFetchTime) > 60 * 60 * 1000;

    if (shouldFetch) {
      fetchFooterData();
    } else {
      // If we have cached data, make sure it has the correct structure
      try {
        const parsedData = JSON.parse(cachedFooterData);
        if (!parsedData.logo) {
          // Transform cached data if it doesn't have the correct structure
          const transformedData = {
            ...parsedData,
            logo: {
              src: parsedData.middleSection?.logoUrl || "",
              alt: parsedData.topSection?.logoText || "Aithm",
            },
          };
          setFooterData(transformedData);
          localStorage.setItem(
            CACHE_KEYS.FOOTER_DATA,
            JSON.stringify(transformedData)
          );
        }
      } catch (error) {
        console.error("Error transforming cached footer data:", error);
        fetchFooterData();
      }
    }
  }, []);

  // Fetch user data with caching
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) {
        setUserData(null);
        // Clear cached user data when no user
        try {
          localStorage.removeItem(CACHE_KEYS.USER_DATA);
        } catch (error) {
          console.error("Error clearing cached user data:", error);
        }
        return;
      }
      try {
        const response = await fetch(
          `https://imagetohtmlaiproject25-d0525ebf3976.herokuapp.com/users?userEmail=${user.email}`
        );
        const data = await response.json();
        if (data && data.length > 0) {
          const userData = data[0];
          setUserData(userData);
          // Cache user data
          try {
            localStorage.setItem(
              CACHE_KEYS.USER_DATA,
              JSON.stringify(userData)
            );
          } catch (error) {
            console.error("Error caching user data:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    // Only fetch user data if we have a user and either no cached data or cached data is stale
    if (user?.email) {
      const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
      const shouldFetch = !cachedUserData || !userData;
      if (shouldFetch) {
        fetchUserData();
      }
    }
  }, [user]);

  const handleSignout = async () => {
    try {
      await signOut(auth);
      // Clear all cached data on logout
      Object.values(CACHE_KEYS).forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error clearing ${key} from localStorage:`, error);
        }
      });
      setUserData(null);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  const closeUserDropdown = () => {
    setUserDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownOpen &&
        !event.target.closest(".user-dropdown-container")
      ) {
        closeUserDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Get logo source with fallback
  const getLogoSrc = () => {
    if (footerLoading) return "";
    if (footerError) return "";
    return footerData?.logo?.src || "";
  };

  // Get logo alt text with fallback
  const getLogoAlt = () => {
    if (footerLoading) return "";
    if (footerError) return "";
    return footerData?.logo?.alt;
  };

  return (
    <>
      <header className="main-header">
        {/*Header-Upper*/}
        <div className="header-upper">
          <div className="container-fluid clearfix">
            <div className="header-inner d-flex align-items-center justify-content-between">
              <div className="logo-outer d-lg-flex align-items-center">
                <div className="logo">
                  <Link to="/">
                    <img
                      src="https://attherate.dev/host-image/uploads/1760377263_ScriptEcho-white__2_.png"
                      alt="Logo"
                      title="Logo"
                    />
                  </Link>
                </div>
              </div>
              <div className="nav-outer clearfix">
                {/* Main Menu */}
                <nav className="main-menu navbar-expand-lg">
                  <div className="navbar-header">
                    {/* Toggle Button */}
                    <button
                      type="button"
                      className="navbar-toggle"
                      data-toggle="collapse"
                      data-target=".navbar-collapse"
                    >
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                    </button>
                    <div className="mobile-logo p-15 m-auto">
                      <a href="index.html">
                        <img
                          src="https://attherate.dev/host-image/uploads/1760377263_ScriptEcho-white__2_.png"
                          alt="Logo"
                          title="Logo"
                        />
                      </a>
                    </div>
                  </div>
                  <div className="navbar-collapse collapse clearfix">
                    <ul className="navigation onepage clearfix">
                      <li>
                        <a href="#home">home</a>
                      </li>
                      <li>
                        <a href="#about">About</a>
                      </li>
                      <li>
                        <a href="#pricing">pricing</a>
                      </li>
                    </ul>
                  </div>
                </nav>
                {/* Main Menu End*/}
              </div>
              {/* Menu Button */}
              <div className="menu-btn-sidebar onepage d-flex align-items-center">
                <Link to="/login" className="theme-btn">
                  Login <i className="fas fa-long-arrow-alt-right" />
                </Link>
               
              </div>
            </div>
          </div>
        </div>
        {/*End Header Upper*/}
      </header>
      
    </>
  );
};
export default NavBar;
